import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { APP_CONSTANTS } from '../constants/enum.constants';
import { CoinInfoSocketResponse, MarketPairSocketResponse } from '../models/socket-response.model';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
    providedIn: 'root'
})
export class WebsocketService {
    private _socket!: Socket;

    private _socketStatusSubject = new BehaviorSubject<boolean>(false);
    socketStatusObs = this._socketStatusSubject.asObservable();

    /**
     * Coin Price Socket Variables
     */
    private _coinInfoSubject = new Subject<CoinInfoSocketResponse>();
    coinInfoObs = this._coinInfoSubject.asObservable();

    private _marketPairSubject = new Subject<MarketPairSocketResponse>();
    marketPairObs = this._marketPairSubject.asObservable();

    /**
     * Establish socket connection with IXFI_WS_API
     * All services in angular are singleton so this connection is made only once
     * and can be used throughout application life-cycle
     */
    constructor(@Inject(PLATFORM_ID) private platformId: object) {
        if (isPlatformBrowser(this.platformId)) this._connectSocket();
    }

    /**
     * This function connects the socket to a room
     * @param room room name
     */
    private _connectSocket(): void {
        this._socket = io(environment.IXFI_WS_API, {
            path: '/socket.io',
            transports: ['websocket', 'polling'],
            upgrade: true
        });

        this._socket.on('connect', () => {
            console.info(`socket connected with Id: ${this._socket.id}`);
            this._socket.emit('authenticate', { token: '' });
            this._socket.on('authenticated', () => {});
            this._socketStatusSubject.next(true);
        });

        this._socket.on('error', err => {
            console.log(`Error while connecting to ixfi socket - ${err}`);
        });
    }

    joinRoom(room: string) {
        if (isPlatformBrowser(this.platformId)) {
            this._socket.emit('subscribe', room);
            this._socket.on(APP_CONSTANTS.PRICE_EVENT, (data: MarketPairSocketResponse | CoinInfoSocketResponse) => {
                if (data['coin_pair_id']) this._marketPairSubject.next(<MarketPairSocketResponse>data);
                else if (data['coin_id']) this._coinInfoSubject.next(<CoinInfoSocketResponse>data);
            });
        }
    }

    /**
     * This function disconnects the connection to the socket
     */
    disconnectFromRoom(room: string) {
        if (isPlatformBrowser(this.platformId)) {
            this._socket.emit('unsubscribe', room);
        }
    }

    disconnectSocket() {
        if (isPlatformBrowser(this.platformId)) {
            this._socket.disconnect();
        }
    }
}
